// src/components/cat/SpellCheckPanel.tsx
import React, { useState, useEffect } from 'react'
import { 
  AlertCircle, 
  CheckCircle2, 
  X, 
  RefreshCw,
  BookOpen,
  Zap,
  ChevronRight,
  Plus,
  Trash2
} from 'lucide-react'
import { spellChecker, SpellingError, SpellCheckResult } from '../../lib/cat/spellChecker'

interface SpellCheckPanelProps {
  isOpen: boolean
  onClose: () => void
  text: string
  language: string
  onApplySuggestion: (originalText: string, replacement: string) => void
  segmentId?: string
}

export const SpellCheckPanel: React.FC<SpellCheckPanelProps> = ({
  isOpen,
  onClose,
  text,
  language,
  onApplySuggestion,
  segmentId,
}) => {
  const [result, setResult] = useState<SpellCheckResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedError, setSelectedError] = useState<SpellingError | null>(null)
  const [showIgnoreList, setShowIgnoreList] = useState(false)
  const [ignoreList, setIgnoreList] = useState<string[]>([])
  
  // Filter settings
  const [showSpelling, setShowSpelling] = useState(true)
  const [showGrammar, setShowGrammar] = useState(true)
  const [showStyle, setShowStyle] = useState(true)

  useEffect(() => {
    if (isOpen && text.trim()) {
      checkSpelling()
    }
  }, [isOpen, text, segmentId])

  useEffect(() => {
    // Load ignore list
    setIgnoreList(spellChecker.getIgnoreList())
  }, [])

  const checkSpelling = async () => {
    setLoading(true)
    setError(null)
    setSelectedError(null)

    try {
      const checkResult = await spellChecker.checkText(text, { language })
      setResult(checkResult)
    } catch (err) {
      console.error('[SpellCheckPanel] Error:', err)
      setError('Failed to check spelling. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleApplySuggestion = (error: SpellingError, replacement: string) => {
    const before = text.substring(0, error.offset)
    const after = text.substring(error.offset + error.length)
    const newText = before + replacement + after
    
    onApplySuggestion(text, newText)
    
    // Re-check after applying
    setTimeout(checkSpelling, 500)
  }

  const handleIgnoreWord = (error: SpellingError) => {
    const word = text.substring(error.offset, error.offset + error.length)
    spellChecker.addToIgnoreList(word)
    setIgnoreList(spellChecker.getIgnoreList())
    
    // Re-check to remove ignored errors
    checkSpelling()
  }

  const handleRemoveFromIgnoreList = (word: string) => {
    spellChecker.removeFromIgnoreList(word)
    setIgnoreList(spellChecker.getIgnoreList())
    checkSpelling()
  }

  const filteredErrors = result?.errors.filter(error => {
    if (!showSpelling && error.category === 'TYPOS') return false
    if (!showGrammar && error.category === 'GRAMMAR') return false
    if (!showStyle && error.category === 'STYLE') return false
    return true
  }) || []

  const errorCounts = {
    spelling: result?.errors.filter(e => e.category === 'TYPOS').length || 0,
    grammar: result?.errors.filter(e => e.category === 'GRAMMAR').length || 0,
    style: result?.errors.filter(e => e.category === 'STYLE').length || 0,
  }

  if (!isOpen) return null

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-slate-700 bg-slate-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold text-white">Spell Check</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-700 rounded-md transition-colors"
          >
            <X className="h-5 w-5 text-slate-200" />
          </button>
        </div>
        <p className="text-xs font-medium text-slate-100 mt-1">
          Powered by LanguageTool • Free API
        </p>
      </div>

      {/* Stats Bar */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-slate-700 bg-slate-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-1">
              <div className={`w-3 h-3 rounded-full ${errorCounts.spelling > 0 ? 'bg-red-500' : 'bg-green-500'}`}></div>
              <span className="text-sm font-semibold text-white">{errorCounts.spelling} Spelling</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className={`w-3 h-3 rounded-full ${errorCounts.grammar > 0 ? 'bg-orange-500' : 'bg-green-500'}`}></div>
              <span className="text-sm font-semibold text-white">{errorCounts.grammar} Grammar</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className={`w-3 h-3 rounded-full ${errorCounts.style > 0 ? 'bg-blue-500' : 'bg-green-500'}`}></div>
              <span className="text-sm font-semibold text-white">{errorCounts.style} Style</span>
            </div>
          </div>
          <button
            onClick={checkSpelling}
            disabled={loading}
            className="p-1 hover:bg-slate-600 rounded-md transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 text-gray-100 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex-shrink-0 px-4 py-2 border-b border-gray-200 flex items-center space-x-2 text-sm bg-white">
        <span className="text-gray-700 font-medium">Show:</span>
        <label className="flex items-center space-x-1 cursor-pointer">
          <input
            type="checkbox"
            checked={showSpelling}
            onChange={(e) => setShowSpelling(e.target.checked)}
            className="rounded text-red-600"
          />
          <span className="text-sm font-semibold text-gray-800">Spelling</span>
        </label>
        <label className="flex items-center space-x-1 cursor-pointer">
          <input
            type="checkbox"
            checked={showGrammar}
            onChange={(e) => setShowGrammar(e.target.checked)}
            className="rounded text-orange-600"
          />
          <span className="text-sm font-semibold text-gray-800">Grammar</span>
        </label>
        <label className="flex items-center space-x-1 cursor-pointer">
          <input
            type="checkbox"
            checked={showStyle}
            onChange={(e) => setShowStyle(e.target.checked)}
            className="rounded text-blue-600"
          />
          <span className="text-sm font-semibold text-gray-800">Style</span>
        </label>
        <button
          onClick={() => setShowIgnoreList(!showIgnoreList)}
          className="ml-auto text-xs text-blue-600 hover:text-blue-700 flex items-center space-x-1"
        >
          <BookOpen className="h-3 w-3" />
          <span>Ignore List ({ignoreList.length})</span>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-white">
        {loading && (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
            <p className="text-sm text-gray-700 mt-2">Checking spelling...</p>
          </div>
        )}

        {error && (
          <div className="p-4 m-4 bg-white border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={checkSpelling}
              className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && filteredErrors.length === 0 && (
          <div className="p-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-medium text-gray-900">No issues found!</p>
            <p className="text-sm text-gray-700 mt-1">Your text looks great.</p>
          </div>
        )}

        {!loading && !error && showIgnoreList && (
          <div className="p-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">Ignored Words</h4>
                {ignoreList.length > 0 && (
                  <button
                    onClick={() => {
                      spellChecker.clearIgnoreList()
                      setIgnoreList([])
                      checkSpelling()
                    }}
                    className="text-xs text-red-600 hover:text-red-700"
                  >
                    Clear All
                  </button>
                )}
              </div>
              {ignoreList.length === 0 ? (
                <p className="text-sm text-gray-600 italic">No ignored words</p>
              ) : (
                <div className="space-y-1">
                  {ignoreList.map((word, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-1 px-2 bg-white rounded border border-gray-200"
                    >
                      <span className="text-sm text-gray-700">{word}</span>
                      <button
                        onClick={() => handleRemoveFromIgnoreList(word)}
                        className="p-1 hover:bg-white rounded transition-colors"
                      >
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && !error && !showIgnoreList && filteredErrors.length > 0 && (
          <div className="divide-y divide-gray-200">
            {filteredErrors.map((error, index) => (
              <div
                key={index}
                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  selectedError === error ? 'bg-blue-50' : ''
                }`}
                onClick={() => setSelectedError(error)}
              >
                {/* Error Header */}
                <div className="flex items-start space-x-3">
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                    error.category === 'TYPOS' ? 'bg-white0' :
                    error.category === 'GRAMMAR' ? 'bg-orange-500' :
                    'bg-blue-500'
                  }`}></div>
                  
                  <div className="flex-1 min-w-0">
                    {/* Error context with highlighting */}
                    <div className="text-sm text-gray-700 mb-2 font-mono bg-gray-100 p-2 rounded">
                      {error.context.text.substring(0, error.context.offset)}
                      <span className="bg-red-200 text-red-900 font-semibold">
                        {error.context.text.substring(
                          error.context.offset,
                          error.context.offset + error.context.length
                        )}
                      </span>
                      {error.context.text.substring(error.context.offset + error.context.length)}
                    </div>

                    {/* Error message */}
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      {error.shortMessage}
                    </p>

                    {/* Category badge */}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      error.category === 'TYPOS' ? 'bg-red-100 text-red-800' :
                      error.category === 'GRAMMAR' ? 'bg-orange-100 text-orange-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {error.category === 'TYPOS' ? 'Spelling' :
                       error.category === 'GRAMMAR' ? 'Grammar' : 'Style'}
                    </span>

                    {/* Suggestions */}
                    {error.replacements.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {error.replacements.slice(0, 3).map((replacement, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleApplySuggestion(error, replacement)
                            }}
                            className="w-full text-left px-3 py-2 bg-white border border-gray-200 rounded-md hover:bg-blue-50 hover:border-blue-300 transition-colors flex items-center justify-between group"
                          >
                            <span className="text-sm text-gray-900 font-medium">
                              {replacement}
                            </span>
                            <Zap className="h-4 w-4 text-gray-700 group-hover:text-blue-600" />
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Ignore button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleIgnoreWord(error)
                      }}
                      className="mt-2 text-xs text-gray-600 hover:text-gray-700 flex items-center space-x-1"
                    >
                      <Plus className="h-3 w-3" />
                      <span>Add to ignore list</span>
                    </button>
                  </div>

                  <ChevronRight className="h-5 w-5 text-gray-700 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 px-4 py-2 border-t border-slate-700 bg-slate-900">
        <p className="text-xs text-slate-200">
          Press <kbd className="px-1.5 py-0.5 bg-slate-700 border border-slate-500 rounded text-xs text-white font-bold">Ctrl+L</kbd> <span className="text-slate-200 font-medium">to toggle spell check</span>
        </p>
      </div>
    </div>
  )
}